import { useEffect, useMemo, useRef, useState } from "react";
import { GeoJSON, MapContainer, Marker, Popup, TileLayer, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { spacedPlaces, stableRank } from "../lib/map-layout";
import { renderToStaticMarkup } from "react-dom/server";
import { Heart, LocateFixed } from "lucide-react";
import { GoogleMapsLink } from "./GoogleMapsLink";
import type { HomeLocation } from "../lib/ai-chat";
import type { PlaceForecast } from "../lib/weather";
import { isSunnyDay, isGoodWeather, sunshineFraction, displayWeatherCode, formatDay, weatherKind, weatherLabel } from "../lib/weather";
import { WeatherFx } from "./WeatherFx";
import { WeatherIcon } from "./WeatherIcon";
import type { MapBounds, RegionBoundary } from "../lib/api";

type Props = {
  fitKey?: string;
  onReferencePlacesChange: (places: PlaceForecast[]) => void;
  home?: HomeLocation;
  goodWeather?: boolean;
  weatherFx?: boolean;
  places: PlaceForecast[];
  dayIndex: number;
  selected?: PlaceForecast;
  favoriteIds: Set<string>;
  onSelect: (place: PlaceForecast) => void;
  onToggleFavorite: (place: PlaceForecast) => void;
  onViewportChange: (bounds: MapBounds) => void;
  regionBoundary?: RegionBoundary;
  viewportLoading?: boolean;
  regionName: string;
};

function escapeLabel(value: string): string { return value.replace(/[&<>"']/g, char => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]!); }

function markerIcon(place: PlaceForecast, dayIndex: number, active: boolean, goodWeather = false) {
  const day = place.days[dayIndex] ?? place.days[0];
  if (!day) return L.divIcon({ className: "weather-marker-empty", html: "" });
  const glyph = renderToStaticMarkup(<WeatherIcon kind={weatherKind(displayWeatherCode(day))} size={25} strokeWidth={1.9} />);
  const where = [place.province, place.region, place.country].filter((part, index, all) => part && all.indexOf(part) === index).join(", ");
  const tooltip = [
    place.name,
    where,
    place.elevation != null ? `${Math.round(place.elevation)} m` : "",
    `${formatDay(day.date, true)}: ${weatherLabel(displayWeatherCode(day))}`,
    `${Math.round(day.temperatureMin)}° / ${Math.round(day.temperatureMax)}° · pioggia ${Math.round(day.precipitationProbability)}%`
  ].filter(Boolean).join("\n");
  const kind = weatherKind(displayWeatherCode(day));
  const bigGlyph = renderToStaticMarkup(<WeatherIcon kind={kind} size={38} strokeWidth={1.8} />);
  const verdict = isGoodWeather(day) ? ["good", "Giornata ideale"] : (kind === "rain" || kind === "storm" || kind === "snow" || day.precipitationProbability >= 60) ? ["bad", "Meglio evitare"] : ["mid", "Variabile"];
  const sunPercent = Math.round(Math.min(1, sunshineFraction(day)) * 100);
  const card = `<div class="tip-card tip-${kind}">
<div class="tip-head"><span class="tip-glyph">${bigGlyph}</span><div class="tip-title"><b>${escapeLabel(place.name)}</b><small>${escapeLabel(where)}${place.elevation != null ? ` · ${Math.round(place.elevation)} m` : ""}</small></div></div>
<div class="tip-main"><strong>${Math.round(day.temperatureMin)}° <i>/</i> ${Math.round(day.temperatureMax)}°</strong><span>${escapeLabel(weatherLabel(displayWeatherCode(day)))}</span></div>
<div class="tip-sun"><span>Sole ${sunPercent}% delle ore di luce</span><div class="tip-bar"><i style="width:${sunPercent}%"></i></div></div>
<div class="tip-metrics"><span><b>${Math.round(day.precipitationProbability)}%</b>pioggia</span><span><b>${day.precipitation.toFixed(1)} mm</b>acqua</span><span><b>${Math.round(day.windMax)} km/h</b>vento</span></div>
<div class="tip-foot"><em class="tip-verdict is-${verdict[0]}">${verdict[1]}</em><span>${escapeLabel(formatDay(day.date, true))}</span></div></div>`;
  return L.divIcon({
    className: "weather-marker-shell",
    html: `<div data-tooltip="${escapeLabel(tooltip)}" data-tooltip-html="${escapeLabel(card)}" class="weather-marker weather-${weatherKind(displayWeatherCode(day))} ${active ? "is-selected" : ""} ${goodWeather ? isSunnyDay(day) ? "is-sunny" : "is-dimmed" : ""}"><span class="weather-glyph">${glyph}</span><strong>${Math.round(day.temperatureMax)}°</strong><small>${Math.round(day.precipitationProbability)}%</small><span class="marker-place-name">${escapeLabel(place.name)}</span></div>`,
    iconSize: [94, 76], iconAnchor: [47, 72], popupAnchor: [0, -50]
  });
}

function ViewportReporter({ onChange }: { onChange: (bounds: MapBounds) => void }) {
  const map = useMapEvents({ moveend: () => report() });
  const report = () => {
    const bounds = map.getBounds();
    onChange({ west: bounds.getWest(), south: bounds.getSouth(), east: bounds.getEast(), north: bounds.getNorth(), zoom: map.getZoom() });
  };
  useEffect(() => { report(); }, []);
  return null;
}

function FitPlaces({ fitKey, places }: { fitKey?: string; places: PlaceForecast[] }) {
  const map = useMap();
  useEffect(() => {
    if (!fitKey || !places.length) return;
    const bounds = L.latLngBounds(places.map(place => [place.latitude, place.longitude] as [number, number]));
    map.fitBounds(bounds, { paddingTopLeft: [60, 170], paddingBottomRight: [60, 140], maxZoom: 11, animate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey, map]);
  return null;
}

function FlyTo({ place }: { place?: PlaceForecast }) {
  const map = useMap();
  useEffect(() => {
    if (place) map.panTo([place.latitude, place.longitude], { animate: true, duration: 0.4 });
  }, [map, place]);
  return null;
}

export function WeatherMap({ fitKey, onReferencePlacesChange, home, goodWeather, weatherFx, places, dayIndex, selected, favoriteIds, onSelect, onToggleFavorite, onViewportChange, regionBoundary, viewportLoading, regionName }: Props) {
  return (
    <div className="map-frame">
      <MapContainer center={[46.48, 11.36]} zoom={8} minZoom={6} maxZoom={15} zoomControl={false} className="map-canvas">
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {regionBoundary && <GeoJSON key={regionBoundary.properties.name} data={regionBoundary} style={{ color: "#e65532", weight: 3, opacity: .9, fillColor: "#ff8a66", fillOpacity: .1 }} />}
        {weatherFx && <WeatherFx places={places} dayIndex={dayIndex} />}
        <ViewportReporter onChange={onViewportChange} />
        <FitPlaces fitKey={fitKey} places={places} />
        <FlyTo place={selected} />
        <WeatherMarkers places={places} dayIndex={dayIndex} selected={selected} goodWeather={goodWeather} home={home} favoriteIds={favoriteIds} onSelect={onSelect} onToggleFavorite={onToggleFavorite} onReferencePlacesChange={onReferencePlacesChange} />
      </MapContainer>
      <button className="map-locate icon-button" aria-label={`Torna su ${regionName}`} onClick={() => { const place = places.find(place => place.region === regionName) ?? places[0]; if (place) onSelect(place); }}><LocateFixed /></button>
      {viewportLoading && <div className="viewport-loading"><span className="spinner" /> Aggiorno l’area visibile</div>}
    </div>
  );
}

function WeatherMarkers({ places, selected, dayIndex, goodWeather, home, favoriteIds, onSelect, onToggleFavorite, onReferencePlacesChange }: Pick<Props, "places" | "selected" | "dayIndex" | "goodWeather" | "home" | "favoriteIds" | "onSelect" | "onToggleFavorite" | "onReferencePlacesChange">) {
  const [revision, setRevision] = useState(0);
  const map = useMapEvents({ moveend: () => setRevision(value => value + 1), resize: () => setRevision(value => value + 1) });
  // Luoghi già mostrati a questo zoom: restano in cima alla priorità, così arrivi di nuovi dati o
  // spostamenti non li fanno sparire.
  const shown = useRef<{ zoom: number; ids: Set<string> }>({ zoom: -1, ids: new Set() });
  const layout = useMemo(() => {
    const size = map.getSize(), zoom = map.getZoom();
    if (shown.current.zoom !== zoom) shown.current = { zoom, ids: new Set() };
    const sticky = shown.current.ids;
    const priority = (place: PlaceForecast) => place.id === selected?.id ? 0 : favoriteIds.has(place.id) ? 1 : sticky.has(place.id) ? 2 : 3;
    const candidates = [...places].sort((a, b) => priority(a) - priority(b) || stableRank(a.id) - stableRank(b.id) || a.id.localeCompare(b.id));
    // Il calcolo copre un'area più grande dello schermo: chi sta ai bordi non cambia vincitore mentre ci si sposta.
    const points = candidates.map(place => { const point = map.latLngToContainerPoint([place.latitude, place.longitude]); return { place, x: point.x, y: point.y }; })
      .filter(point => point.x >= -size.x && point.x <= size.x * 2 && point.y >= -size.y && point.y <= size.y * 2);
    const kept = spacedPlaces(points);
    kept.forEach(place => sticky.add(place.id));
    const position = new Map(points.map(point => [point.place.id, point]));
    const rendered = kept.filter(place => { const point = position.get(place.id)!; return point.x >= -100 && point.x <= size.x + 100 && point.y >= -100 && point.y <= size.y + 100; });
    const references = rendered.filter(place => { const point = position.get(place.id)!; return point.x >= 48 && point.x <= size.x - 48 && point.y >= 150 && point.y <= size.y - 125; });
    return { rendered, references };
  }, [places, selected?.id, favoriteIds, map, revision]);
  const references = layout.references;
  useEffect(() => { onReferencePlacesChange(references); }, [references, onReferencePlacesChange]);
  const icons = useMemo(() => new Map(layout.rendered.map(place => [place.id, markerIcon(place, dayIndex, place.id === selected?.id, goodWeather)])), [layout.rendered, dayIndex, selected?.id, goodWeather]);
  return <>{layout.rendered.map((place) => {
          const day = place.days[dayIndex] ?? place.days[0];
          if (!day) return null;
          return (
            <Marker key={place.id} position={[place.latitude, place.longitude]} icon={icons.get(place.id)!} eventHandlers={{ click: () => onSelect(place) }}>
              <Popup className="place-popup" minWidth={230}>
                <div className="popup-head">
                  <div><span className="eyebrow">{place.province ?? place.region}</span><h3>{place.name}</h3></div>
                  <GoogleMapsLink place={place} home={home} /><button className={`icon-button ${favoriteIds.has(place.id) ? "is-favorite" : ""}`} onClick={() => onToggleFavorite(place)} aria-label={favoriteIds.has(place.id) ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}><Heart fill={favoriteIds.has(place.id) ? "currentColor" : "none"} /></button>
                </div>
                <div className="popup-weather"><WeatherIcon kind={weatherKind(displayWeatherCode(day))} size={34} /><div><strong>{Math.round(day.temperatureMin)}–{Math.round(day.temperatureMax)}°</strong><span>{weatherLabel(displayWeatherCode(day))}</span></div></div>
                <div className="popup-metrics"><span><b>{day.precipitation.toFixed(1)} mm</b> pioggia</span><span><b>{day.sunshineHours.toFixed(1)} h</b> sole</span><span><b>{Math.round(day.windMax)} km/h</b> vento</span></div>
                <small className="muted">{formatDay(day.date, true)}</small>
              </Popup>
            </Marker>
          );
        })}</>;
}
